import { useIdioma, type Traducir } from '../../i18n/idioma';
import React, { useRef } from 'react';
import type { Photo } from './media';
import { useSectionMotion, EASE, REPITE } from './motion';
import DistorsionLiquida from './DistorsionLiquida';

interface FotoBandaProps {
    foto: Photo;
    /** `alta` para los respiros narrativos, `media` cuando acompaña a un bloque. */
    alto?: 'media' | 'alta';
    /** La foto es la primera imagen del recorrido tras el vídeo: no diferir. */
    prioritaria?: boolean;
    children: React.ReactNode;
}

/**
 * Banda fotográfica a sangre.
 *
 * Existe para romper el ritmo: entre dos folds de texto, una fotografía a todo
 * lo ancho es lo que evita que la página se lea como diez secciones con la
 * misma silueta —que era exactamente el diagnóstico del diseño anterior—.
 *
 * Dos recorridos, los dos atados al scroll y los dos sobre `transform` —nunca
 * `top` ni `background-position`, que provocarían un relayout por cuadro—:
 * el ACOMODO (la imagen entra adelantada y se asienta al llegar a ella) y el
 * PARALAJE (se desplaza más despacio que la página mientras pasa).
 */
const FotoBanda: React.FC<FotoBandaProps> = ({
    foto,
    alto = 'alta',
    prioritaria = false,
    children,
}) => {
    const { t } = useIdioma();
    const imgRef = useRef<HTMLImageElement>(null);

    /* `object-position` de la foto, traducido a 0–1 para el shader. La fuente
       de verdad sigue siendo `media.ts`: si allí se recuadra una foto, la
       versión distorsionada se recuadra con ella y no hay dos encuadres que
       mantener sincronizados a mano. */
    const posicion = React.useMemo<[number, number]>(() => {
        const [x, y] = foto.position.split(/\s+/);
        return [(parseFloat(x) || 50) / 100, (parseFloat(y) || 50) / 100];
    }, [foto.position]);
    const scope = useSectionMotion<HTMLElement>(({ gsap, scope, motion, reveal, full }) => {
        /* ── El acomodo ──────────────────────────────────────────────────
           La fotografía entra «adelantada» —un 22 % más grande de lo que le
           toca— y se acomoda a su sitio al llegar a pantalla.

           DOS CORRECCIONES sobre el primer intento, las dos pedidas por el
           equipo («no se nota nada, y no es constante»):

           1. Va con TEMPORIZADOR, no atado al scroll. Con `scrub` el recorrido
              se repartía entre cientos de píxeles de desplazamiento, así que
              nunca ocurría «de golpe» y literalmente no se percibía. Con
              duración propia el acomodo es un suceso: pasa, se ve, y termina.
           2. Se REPITE cada vez que la banda vuelve a entrar en pantalla —
              subiendo o bajando—, no una sola vez en la vida de la página.

           TERCER AJUSTE (petición: «que se sienta un poco más suave»): el
           recorrido baja de 28 % a 22 %, la duración sube de 1,25 s a 1,9 s y
           la curva pasa de `power2.out` a `power2.inOut`. La clave es la curva:
           con `out` puro el 20 % del recorrido se gastaba en los primeros
           60 ms y eso se percibe como un tirón. Entrando también despacio, el
           acomodo se desliza en vez de arrancar de golpe.

           Con movimiento reducido baja a un 12 %: se sigue viendo, pesa menos. */
        gsap.fromTo(
            scope.querySelector('[data-zoom]'),
            { scale: full ? 1.22 : 1.12 },
            {
                scale: 1,
                duration: full ? 1.9 : 1.2,
                ease: EASE.asiento,
                scrollTrigger: {
                    trigger: scope,
                    start: 'top 88%',
                    end: 'bottom 12%',
                    toggleActions: REPITE,
                },
            },
        );

        /* Las palabras del enunciado suben desde detrás del renglón, y también
           se repiten en cada visita. */
        gsap.from(scope.querySelectorAll('[data-frase="banda"]'), {
            yPercent: full ? 112 : 0,
            autoAlpha: full ? 1 : 0,
            duration: full ? 1.2 : 0.75,
            stagger: full ? 0.065 : 0.04,
            ease: full ? EASE.titular : EASE.out,
            scrollTrigger: {
                trigger: scope,
                start: 'top 68%',
                end: 'bottom 12%',
                toggleActions: REPITE,
            },
        });

        reveal('[data-copia]', { start: 'top 62%' });

        motion(() => {
            /* La copia se desplaza MÁS que la imagen y en sentido contrario.
               Dos velocidades distintas en el mismo encuadre es lo que produce
               la sensación de profundidad; con todo moviéndose igual, la banda
               se lee como una calcomanía. */
            gsap.fromTo(
                scope.querySelector('[data-capa-copia]'),
                { yPercent: 14 },
                {
                    yPercent: -14,
                    ease: EASE.none,
                    scrollTrigger: {
                        trigger: scope,
                        start: 'top bottom',
                        end: 'bottom top',
                        scrub: 1,
                    },
                },
            );

            /* El paralaje mueve TODO lo que hay dentro de `[data-zoom]`: la
               <img> y el lienzo de la distorsión. Se selecciona por estructura
               y no por un atributo en el componente, porque TypeScript NO valida
               los atributos con guion en componentes propios — un `data-foto`
               pasado como prop se ignora en silencio y el lienzo se quedaba
               quieto mientras la imagen se movía. */
            gsap.fromTo(
                scope.querySelectorAll('[data-zoom] > *'),
                { yPercent: -7 },
                {
                    yPercent: 7,
                    ease: EASE.none,
                    scrollTrigger: {
                        trigger: scope,
                        start: 'top bottom',
                        end: 'bottom top',
                        /* `scrub: 1` en vez de `true`: la imagen tarda un
                           segundo en alcanzar la posición que le toca, así que
                           persigue al scroll con un poco de retraso en vez de
                           ir clavada a la rueda. Es lo que separa un paralaje
                           que se siente mecánico de uno que se siente fluido. */
                        scrub: 1,
                    },
                },
            );
        });
    });

    return (
        <section
            ref={scope}
            data-fondo="navy"
            className={`relative flex items-end overflow-hidden bg-navy-850 ${
                alto === 'alta'
                    ? 'min-h-[clamp(26rem,64vh,42rem)]'
                    : 'min-h-[clamp(20rem,48vh,32rem)]'
            }`}
        >
            {/* Tres capas, y cada una tiene su motivo:
                · el marco recorta (`overflow-hidden`), para que el acomodo no
                  desborde la banda;
                · `[data-zoom]` se encarga SOLO de la escala;
                · la <img> se encarga SOLO del paralaje.
                Separadas, cada transform vive en su propia capa del compositor.
                Juntas en un mismo elemento, el navegador tiene que recalcular la
                matriz completa en cada cuadro y el acomodo tiembla. */}
            <div data-marco-foto className="absolute inset-0 overflow-hidden">
                <div data-zoom className="absolute inset-0 will-change-transform">
                    <img
                        ref={imgRef}
                        data-foto
                        src={foto.src}
                        srcSet={foto.srcSet}
                        /* En pantallas grandes la foto ocupa el ancho entero y
                           eso es lo que se declara. En el teléfono se pide a
                           propósito menos: un móvil de 390 px a 3x reclamaría
                           1.170 px y el navegador saltaría al escalón de 1.440,
                           334 KB para UNA foto. Declarando 75vw baja al de 960
                           (2,5x real), y son 285 KB menos en las tres.
                           Es una foto de fondo a sangre, bajo un degradado
                           opaco y con un titular grande encima: a esa densidad
                           la diferencia no se ve, y los datos móviles sí se
                           notan. En un retrato o en una captura con texto esto
                           no se haría. */
                        sizes="(max-width: 640px) 75vw, 100vw"
                        alt={t((foto.alt) as Parameters<Traducir>[0])}
                        loading={prioritaria ? 'eager' : 'lazy'}
                        decoding="async"
                        /* Sin esto la GPU se niega a leer la imagen («contains
                           cross-origin data») y la distorsión no puede
                           existir. Unsplash responde
                           Access-Control-Allow-Origin: *, comprobado. */
                        crossOrigin="anonymous"
                        style={{ objectPosition: foto.position }}
                        className="absolute inset-x-0 -top-[7%] h-[114%] w-full object-cover"
                    />
                    {/* Distorsión líquida bajo el puntero. Ocupa EXACTAMENTE la
                        misma caja que la <img> y lleva su misma marca, así que
                        comparte escala y paralaje: el relevo no se nota. */}
                    <DistorsionLiquida
                        fuenteRef={imgRef}
                        posicion={posicion}
                        className="absolute inset-x-0 -top-[7%] h-[114%] w-full"
                    />
                </div>
            </div>
            {/* Velo en degradado: opaco abajo, donde va el texto, y casi limpio
                arriba, para que la fotografía se vea. Un velo plano al 55 % la
                apagaba entera para resolver un problema que solo existe en el
                tercio inferior. */}
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy-850 via-navy-950/45 to-navy-950/15" />

            <div
                data-capa-copia
                className="relative w-full px-5 py-[clamp(2.5rem,6vw,4.5rem)] sm:px-8"
            >
                <div className="mx-auto max-w-[1240px]">{children}</div>
            </div>
        </section>
    );
};

export default FotoBanda;
